import { ExternalLink, Linkedin, Network, Pencil, Plus, Trash2 } from "lucide-react";
import { TIERS } from "../data/broker";

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex gap-2 text-[11px] leading-tight">
      <span className="text-zinc-500 uppercase font-mono tracking-[0.18em] w-[74px] shrink-0">
        {label}
      </span>
      <span className="text-zinc-200 break-words">{value}</span>
    </div>
  );
}

export default function ContactPopup({
  contact: c,
  stateName,
  connectionCount = 0,
  stateNetworks, // { tpas: [{tpa, carriers[]}], carrierCount, tpaCount } | undefined
  stateMarketTier, // { tier_1, tier_2, tier_3, independent_tpas, notes } | undefined
  onEdit,
  onDelete,
  onAddConnection,
  onViewConnections,
}) {
  const isSalesTeam = c.kind === "sales_team";
  const isEmployer = c.kind === "employer";
  const tier = TIERS[c.tier] || TIERS.t5;
  if (isEmployer) {
    // Employer customers come from HubSpot *companies*: no tier, no
    // connections, nothing to edit in-app.
    return (
      <div className="min-w-[280px] max-w-[320px] font-sans" data-testid="employer-popup">
        <div className="font-mono text-[9px] uppercase tracking-[0.22em] mb-1 text-amber-300">
          Employer · Customer
        </div>
        <div className="font-semibold text-zinc-50 text-[13px] leading-tight break-words">
          {c.name || "—"}
        </div>
        <div className="space-y-1 mt-2">
          <Field label="Industry" value={c.industry || "Unspecified"} />
          <Field label="Location" value={[c.city, stateName || c.state].filter(Boolean).join(", ")} />
          <Field label="Employees" value={c.employees ? String(c.employees) : ""} />
          <Field label="Domain" value={c.domain} />
        </div>
        {c.hubspot_link && (
          <a
            href={c.hubspot_link}
            target="_blank"
            rel="noreferrer"
            data-testid="employer-popup-hubspot"
            className="mt-3 block font-mono text-[10px] tracking-[0.16em] uppercase text-amber-300 hover:text-amber-200"
          >
            Open in HubSpot →
          </a>
        )}
      </div>
    );
  }
  return (
    <div className="min-w-[280px] max-w-[320px] font-sans" data-testid="contact-popup">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div
            className="font-mono text-[9px] uppercase tracking-[0.22em] mb-1"
            style={{ color: tier.color }}
          >
            {tier.short} · {tier.label}
          </div>
          <div className="font-semibold text-zinc-50 text-[13px] leading-tight break-words">
            {c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "—"}
          </div>
          <div className="text-[11px] text-zinc-400 mt-0.5 truncate">
            {c.company_name}
          </div>
        </div>
      </div>

      <div className="space-y-1 mt-2">
        {!isSalesTeam && (
          <>
            <Field label="Territory" value={c.territory ? c.territory.replace(/^\w/, (l) => l.toUpperCase()) : ""} />
            <Field label="State" value={stateName || c.state} />
            <Field label="Email" value={c.email} />
            <Field label="Phone" value={c.phone} />
            <Field label="Owner" value={c.contact_owner} />
            <Field label="Last Act." value={c.last_activity_date} />
            <Field label="Lifecycle" value={c.lifecycle_stage} />
          </>
        )}
        {isSalesTeam && (
          <div className="text-[11px] text-[#B85450] font-mono tracking-[0.16em] uppercase">
            Virtue Health · Sales Team
          </div>
        )}
        {c.note && <Field label="Note" value={c.note} />}
      </div>

      {!isSalesTeam && stateMarketTier && (stateMarketTier.tier_1 || stateMarketTier.tier_2) && (
        <div
          className="mt-3 pt-2 border-t border-amber-400/20"
          data-testid="popup-market-tier"
        >
          <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-amber-200 mb-1">
            Market Tier · {stateName || c.state}
          </div>
          <div className="text-[10.5px] leading-tight text-zinc-300">
            <span className="text-amber-300 font-mono tracking-[0.1em]">T1</span>{" "}
            {stateMarketTier.tier_1 || "—"}
            {stateMarketTier.tier_2 && (
              <>
                {" · "}
                <span className="text-amber-300/80 font-mono tracking-[0.1em]">T2</span>{" "}
                {stateMarketTier.tier_2}
              </>
            )}
            {stateMarketTier.tier_3 && (
              <>
                {" · "}
                <span className="text-amber-300/60 font-mono tracking-[0.1em]">T3</span>{" "}
                {stateMarketTier.tier_3}
              </>
            )}
          </div>
          {stateMarketTier.notes && (
            <div className="text-[9.5px] text-zinc-500 italic mt-0.5">
              {stateMarketTier.notes}
            </div>
          )}
        </div>
      )}

      {!isSalesTeam && stateNetworks && stateNetworks.tpaCount > 0 && (
        <div
          className="mt-3 pt-2 border-t border-emerald-400/20"
          data-testid="popup-networks"
        >
          <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-emerald-200 mb-1.5">
            Networks in {stateName || c.state}
          </div>
          <div className="space-y-0.5">
            {stateNetworks.tpas.slice(0, 4).map((row) => (
              <div key={row.tpa} className="text-[10.5px] leading-tight">
                <span className="font-mono uppercase tracking-[0.1em] text-emerald-200/90">
                  {row.tpa}
                </span>
                <span className="text-zinc-400"> · {row.carriers.join(", ")}</span>
              </div>
            ))}
            {stateNetworks.tpas.length > 4 && (
              <div className="text-[9.5px] text-zinc-500 font-mono tracking-[0.12em] uppercase">
                +{stateNetworks.tpas.length - 4} more · see Networks tab
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex gap-2 mt-3 flex-wrap">
        {c.hubspot_contact_link && (
          <a
            href={c.hubspot_contact_link}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 px-2 py-1 border border-orange-400/40 text-orange-300 hover:bg-orange-400/10 font-mono text-[10px] uppercase tracking-[0.16em]"
            data-testid="popup-hubspot-contact"
          >
            <ExternalLink className="w-3 h-3" /> HubSpot
          </a>
        )}
        {c.company_hubspot_link && (
          <a
            href={c.company_hubspot_link}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 px-2 py-1 border border-orange-400/40 text-orange-300 hover:bg-orange-400/10 font-mono text-[10px] uppercase tracking-[0.16em]"
            data-testid="popup-hubspot-company"
          >
            <ExternalLink className="w-3 h-3" /> Company
          </a>
        )}
        {c.linkedin_url && (
          <a
            href={c.linkedin_url}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 px-2 py-1 border border-sky-400/40 text-sky-300 hover:bg-sky-400/10 font-mono text-[10px] uppercase tracking-[0.16em]"
            data-testid="popup-linkedin"
          >
            <Linkedin className="w-3 h-3" /> LinkedIn
          </a>
        )}
      </div>

      <div className="flex gap-2 mt-3 pt-2 border-t border-white/10">
        <button
          type="button"
          onClick={() => onAddConnection && onAddConnection(c.id)}
          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 border border-cyan-400/40 hover:border-cyan-300 hover:bg-cyan-400/10 text-cyan-200 font-mono text-[10px] uppercase tracking-[0.18em]"
          data-testid="popup-add-connection"
        >
          <Plus className="w-3 h-3" /> Connect
        </button>
        <button
          type="button"
          onClick={() => onViewConnections && onViewConnections(c.id)}
          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 border border-white/15 hover:border-fuchsia-400/60 hover:text-fuchsia-300 text-zinc-300 font-mono text-[10px] uppercase tracking-[0.18em]"
          data-testid="popup-view-connections"
        >
          <Network className="w-3 h-3" /> Links · {connectionCount}
        </button>
      </div>

      <div className="flex justify-between gap-2 mt-2">
        <button
          type="button"
          onClick={() => onEdit && onEdit(c.id)}
          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 border border-white/15 hover:border-cyan-400/60 hover:text-cyan-300 text-zinc-300 font-mono text-[10px] uppercase tracking-[0.18em]"
          data-testid="popup-edit-contact"
        >
          <Pencil className="w-3 h-3" /> Edit
        </button>
        <button
          type="button"
          onClick={() => {
            if (window.confirm("Delete this contact?")) onDelete && onDelete(c.id);
          }}
          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 border border-white/15 hover:border-rose-400/60 hover:text-rose-300 text-zinc-300 font-mono text-[10px] uppercase tracking-[0.18em]"
          data-testid="popup-delete-contact"
        >
          <Trash2 className="w-3 h-3" /> Delete
        </button>
      </div>
    </div>
  );
}
